import {
  ApiClientError,
  isApiClientError,
} from "@imi/api-client/ApiClientError.js";
import { logger } from "@src/shared/helpers/logger";
import { TFunction } from "i18next";
import { isError } from "radashi";
import type React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  type ErrorResponse,
  isRouteErrorResponse,
  useParams,
} from "react-router";
import { ErrorStatus } from "./ErrorStatus";

type StatusHandler = (info: {
  error: ApiClientError | ErrorResponse;
  params: Record<string, string | undefined>;
  t: TFunction;
}) => React.JSX.Element | null;

function isChunkError(error: unknown): boolean {
  if (!isError(error)) return false;
  const message = error.message.toLowerCase();
  return [
    "ChunkLoadError",
    "disallowed MIME type",
    "dynamically imported module",
    "Failed to load module script",
    "Unable to preload CSS",
  ].some((msg) => message.includes(msg.toLowerCase()));
}

const statusHandlers: Record<number, StatusHandler> = {
  401: ({ error, t }) => (
    <ErrorStatus
      error={error}
      logout
      message={t("boundaries.unauthorized.message")}
      title={t("boundaries.unauthorized.title")}
    />
  ),
  404: ({ error, t }) => (
    <ErrorStatus
      error={error}
      message={t("boundaries.notFound.message")}
      title={t("boundaries.notFound.title")}
    />
  ),
  500: ({ error, t }) => (
    <ErrorStatus
      error={error}
      message={t("boundaries.serverError.message")}
      retryable
      title={t("boundaries.serverError.title")}
    />
  ),
};

const defaultStatusHandler: StatusHandler = ({ error, params, t }) => {
  if (error.status in statusHandlers) {
    return statusHandlers[error.status]({ error, params, t });
  }

  return (
    <ErrorStatus
      error={error}
      message={t("boundaries.unexpected.message")}
      retryable
      title={t("boundaries.unexpected.title")}
    />
  );
};

export function AppErrorFallback({ error }: { error: unknown }) {
  const { t } = useTranslation("errors");
  const params = useParams();
  const chunkError = isChunkError(error);
  logger.error(error);

  useEffect(() => {
    if (!chunkError) return;
    const key = "chunk-reload-at";
    const last = Number(sessionStorage.getItem(key) || 0);
    if (Date.now() - last > 10_000) {
      sessionStorage.setItem(key, String(Date.now()));
      window.location.reload();
    }
  }, [chunkError]);

  if (isApiClientError(error)) {
    return (statusHandlers?.[error.status] ?? defaultStatusHandler)({
      error: error,
      params,
      t,
    });
  }

  if (chunkError) {
    return (
      <ErrorStatus
        message={t("boundaries.chunkError.message")}
        retryable
        title={t("boundaries.chunkError.title")}
      />
    );
  }

  return isRouteErrorResponse(error) ? (
    (statusHandlers?.[error.status] ?? defaultStatusHandler)({
      error: error,
      params,
      t,
    })
  ) : (
    <ErrorStatus
      error={error}
      message={t("boundaries.unexpected.message")}
      retryable
      title={t("boundaries.unexpected.title")}
    />
  );
}
